import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Topbar } from "../components/shell/Topbar";
import { useLayoutContext } from "../layouts/DashboardLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useSession } from "../lib/auth-client";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { ChevronDown } from "lucide-react";
import { cn } from "@/packages/utils/cn";

interface Member {
  id: string;
  role: "owner" | "admin" | "member";
  user: { id: string; name: string; email: string };
}

interface Invite {
  id: string;
  email: string;
  role: string;
  expiresAt: string;
  token: string;
}

const ROLE_TONE: Record<string, string> = {
  owner: "text-signal-red",
  admin: "text-signal-warn",
  member: "text-signal-ok",
};

export function TeamSettingsPage() {
  const { teamId } = useParams();
  const { teams } = useLayoutContext();
  const { data: session } = useSession();
  const team = teams.find((t) => t.id === teamId);
  const [members, setMembers] = useState<Member[]>([]);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [showInvite, setShowInvite] = useState(false);

  const refresh = () => {
    if (!teamId) return;
    fetch(`/api/teams/${teamId}/members`).then((r) => r.json()).then(setMembers);
    fetch(`/api/teams/${teamId}/invites`).then((r) => r.json()).then(setInvites);
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId]);

  if (!team) return null;

  const me = members.find((m) => m.user.id === session?.user.id);
  const myRole = me?.role;
  const canManage = myRole === "owner" || myRole === "admin";

  const setRole = async (memberId: string, role: "admin" | "member") => {
    const res = await fetch(`/api/teams/${teamId}/members/${memberId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ role }),
    });
    if (!res.ok) alert((await res.json()).error ?? "Failed");
    refresh();
  };

  const removeMember = async (memberId: string) => {
    if (!confirm("Remove this member?")) return;
    const res = await fetch(`/api/teams/${teamId}/members/${memberId}`, {
      method: "DELETE",
    });
    if (!res.ok) alert((await res.json()).error ?? "Failed");
    refresh();
  };

  const revokeInvite = async (id: string) => {
    await fetch(`/api/teams/${teamId}/invites/${id}`, { method: "DELETE" });
    refresh();
  };

  return (
    <>
      <Topbar
        eyebrow="Team settings"
        title={team.name}
        crumbs={[
          { label: "Teams", to: "/teams" },
          { label: team.name },
          { label: "Settings" },
        ]}
        actions={
          canManage && (
            <Button size="sm" onClick={() => setShowInvite(true)}>
              + Invite member
            </Button>
          )
        }
      />
      <div className="px-4 sm:px-8 py-6 max-w-3xl space-y-8">
        <section>
          <h2 className="font-serif text-xl font-semibold tracking-tight mb-3">
            Members ({members.length})
          </h2>
          <div className="bg-card-paper border border-rule rounded-lg overflow-hidden">
            {members.map((m, i) => (
              <div
                key={m.id}
                className={cn(
                  "grid grid-cols-[auto_1fr_auto_auto] gap-3 px-4 py-3 items-center",
                  i > 0 && "border-t border-rule-soft",
                )}
              >
                <span className="size-9 rounded-full bg-signal-lavender text-paper text-xs font-bold flex items-center justify-center">
                  {(m.user.name || m.user.email).slice(0, 2).toUpperCase()}
                </span>
                <div>
                  <div className="font-semibold text-sm">{m.user.name}</div>
                  <div className="font-mono text-xs text-muted-foreground">
                    {m.user.email}
                  </div>
                </div>
                <div className={`font-mono text-[11px] uppercase tracking-wider ${ROLE_TONE[m.role]}`}>
                  ● {m.role}
                </div>
                {canManage && m.user.id !== session?.user.id && m.role !== "owner" && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <ChevronDown className="size-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      {m.role === "member" && (
                        <DropdownMenuItem onSelect={() => setRole(m.id, "admin")}>
                          Promote to admin
                        </DropdownMenuItem>
                      )}
                      {m.role === "admin" && (
                        <DropdownMenuItem onSelect={() => setRole(m.id, "member")}>
                          Demote to member
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem
                        onSelect={() => removeMember(m.id)}
                        className="text-signal-red"
                      >
                        Remove from team
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            ))}
          </div>
        </section>

        {invites.length > 0 && (
          <section>
            <h2 className="font-serif text-xl font-semibold tracking-tight mb-3">
              Pending invites ({invites.length})
            </h2>
            <div className="bg-card-paper border border-rule rounded-lg overflow-hidden">
              {invites.map((inv, i) => (
                <div
                  key={inv.id}
                  className={cn(
                    "grid grid-cols-[1fr_auto_auto_auto] gap-3 px-4 py-3 items-center",
                    i > 0 && "border-t border-rule-soft",
                  )}
                >
                  <div>
                    <div className="font-mono text-sm">{inv.email}</div>
                    <div className="text-xs text-muted-foreground">
                      Expires {new Date(inv.expiresAt).toLocaleDateString()}
                    </div>
                  </div>
                  <span className={`font-mono text-[11px] uppercase ${ROLE_TONE[inv.role]}`}>
                    ● {inv.role}
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      navigator.clipboard.writeText(
                        `${window.location.origin}/invites?token=${inv.token}`,
                      )
                    }
                  >
                    Copy link
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => revokeInvite(inv.id)}>
                    Revoke
                  </Button>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>

      {showInvite && canManage && (
        <InviteDialog
          teamId={teamId!}
          onClose={() => setShowInvite(false)}
          onCreated={() => {
            setShowInvite(false);
            refresh();
          }}
        />
      )}
    </>
  );
}

function InviteDialog({
  teamId,
  onClose,
  onCreated,
}: {
  teamId: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"admin" | "member">("member");
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    const res = await fetch(`/api/teams/${teamId}/invites`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, role }),
    });
    if (!res.ok) {
      setError((await res.json()).error ?? "Failed");
      return;
    }
    onCreated();
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Invite a teammate</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Email</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Role</Label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as "admin" | "member")}
              className="w-full border border-rule rounded-md px-2 py-1.5 bg-card-paper"
            >
              <option value="member">Member — read all, toggle flags</option>
              <option value="admin">Admin — manage everything except owner</option>
            </select>
          </div>
          {error && <div className="text-sm text-signal-red">{error}</div>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!email}>
            Send invite
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
